import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, RefreshCw, ArrowRight, Plus, Check } from 'lucide-react'
import { useScreenHeader } from '../hooks/useScreenHeader'
import { useStopScroll } from '../hooks/useStopScroll'
import {
  useProgressSummary,
  useSmartQueueSession,
  useSessionStatus,
  useStartSmartQueue,
  useStartTopicTrain,
  useVocabularyMap,
  useRecentDecks,
} from '../hooks/useProgress'
import { useCreateDeck } from '../hooks/useDecks'
import { useAuthStore, selectUser } from '../stores/authStore'
import { useLocale } from '../i18n/context'
import WeakSpots from '../components/shared/WeakSpots'
import type { WeakSpotItem } from '../components/shared/WeakSpots'
import DeckProgressBar from '../components/shared/DeckProgressBar'
import DailyGoalFlame from '../components/shared/DailyGoalFlame'
import type { ProgressSummary, DeckCreate, RecentDeck } from '../types'
import AddContentSheet from '../components/shared/AddContentSheet'
import DeckCreateModal from '../components/DeckCreateModal'
import CSVImportModal from '../components/shared/CSVImportModal'

// ── Constants ──────────────────────────────────────────────────────────────────

const SESSION_TARGET = 40

// ── Helpers ────────────────────────────────────────────────────────────────────

function computeSessionPreview(summary: ProgressSummary) {
  const review = Math.min(summary.due_review_count, SESSION_TARGET)
  const newCards = Math.min(summary.new_cards_available, SESSION_TARGET - review)
  return { review, newCards, total: review + newCards }
}

// ~6 cards per minute matches the ux_scenario example (40 cards ≈ 7 min).
function minuteEstimate(cards: number): number {
  return Math.max(1, Math.ceil(cards / 6))
}

// Time-of-day greeting. Accepts tr so the locale switches without re-defining the helper.
function greetingFor(hour: number, greetings: { morning: string; afternoon: string; evening: string }): string {
  if (hour < 12) return greetings.morning
  if (hour < 18) return greetings.afternoon
  return greetings.evening
}

// First name from a possibly-full display name; empty-safe.
function firstName(displayName?: string | null): string | null {
  const first = (displayName ?? '').trim().split(/\s+/)[0]
  return first || null
}

// ── Daily contract block — the single informational block on Home ────────────────
// Compact contract line: greeting + reviewed-today-vs-goal + streak. Non-interactive
// (no buttons — "one primary CTA per screen"). Reads the same ProgressSummary the
// bottom-nav ring consumes, so `today_resolutions` here always agrees with the ring.
// Always rendered, even for a brand-new user (0/goal, unlit flame, 0-day streak) —
// the daily goal is a contract and must be visible from the first second (ux_scenario).
function DailyContract({
  summary,
  displayName,
  userId,
}: {
  summary: ProgressSummary
  displayName?: string | null
  userId: string
}) {
  const { tr } = useLocale()
  const name = firstName(displayName)
  const greeting = greetingFor(new Date().getHours(), tr.home.greeting)

  const done = summary.today_resolutions
  const goal = summary.daily_card_goal || 40
  const streak = summary.streak_days
  // "Reached or exceeded" — never strict equality (ADR-054); overachieving stays lit.
  const goalMet = done >= goal

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h1 style={{
        fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', margin: 0,
        color: 'var(--text-primary)',
      }}>
        {greeting}{name ? `, ${name}` : ''}
      </h1>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '14px 18px', borderRadius: 16,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
      }}>
        {/* reviewed today vs. daily goal — numerator matches the nav ring */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            {done}
          </span>
          <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
            {tr.home.today(goal)}
          </span>
        </div>

        <span aria-hidden style={{ color: 'var(--border)' }}>·</span>
        <DailyGoalFlame goalMet={goalMet} streakDays={streak} userId={userId} />
      </div>
    </div>
  )
}

// ── Shared button style ────────────────────────────────────────────────────────

const ctaBtnStyle = (disabled: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
  // The quantified Study label ("Study · 40 cards · ~7 min") is the widest CTA text.
  // whiteSpace:nowrap guarantees it never orphans a word onto a second line; the
  // clamp()'d font-size + horizontal padding let it shrink to fit one line on narrow
  // phones (down to ~320px) while keeping the full-size pill on desktop.
  width: '100%', padding: '20px clamp(20px, 6vw, 48px)', borderRadius: 50,
  fontSize: 'clamp(0.9rem, 4.2vw, 1.15rem)', fontWeight: 700, letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  transition: 'opacity 0.15s, transform 0.1s',
  minHeight: 72,
})

// Scale press feedback handler (mousedown/mouseup/mouseleave trio)
function pressIn(e: React.MouseEvent<HTMLButtonElement>, disabled: boolean) {
  if (!disabled) (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.97)'
}
function pressOut(e: React.MouseEvent<HTMLButtonElement>) {
  ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
}

// ── Loading skeleton ───────────────────────────────────────────────────────────

function HomePageSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '32px 0', width: '100%' }}>
      <div style={{ height: 14, width: 120, borderRadius: 8, background: 'var(--border)', animation: 'pulse 1.5s ease-in-out infinite', margin: '0 auto' }} />
      <div style={{ height: 72, borderRadius: 50, background: 'var(--border)', animation: 'pulse 1.5s ease-in-out infinite' }} />
    </div>
  )
}

// ── Error components ───────────────────────────────────────────────────────────

function HomePageError({ onRetry }: { onRetry: () => void }) {
  const { tr } = useLocale()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, padding: '32px 0', textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: 16, background: 'var(--danger-soft)', color: 'var(--danger)' }}>
        <AlertCircle size={26} />
      </div>
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
          {tr.home.loadError}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{tr.home.loadErrorSub}</p>
      </div>
      <button
        onClick={onRetry}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', borderRadius: 50, fontWeight: 600, fontSize: 14, background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', cursor: 'pointer', minHeight: 44 }}
      >
        <RefreshCw size={14} /> {tr.home.retry}
      </button>
    </div>
  )
}

// Inline session-creation error screen — coding rule 13
function StudyError({ onRetry, onBack }: { onRetry: () => void; onBack: () => void }) {
  const { tr } = useLocale()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '32px 0', textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: 16, background: 'var(--danger-soft)', color: 'var(--danger)' }}>
        <AlertCircle size={24} />
      </div>
      <div>
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
          {tr.home.sessionError}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
          {tr.home.sessionErrorSub}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={onBack}
          style={{ padding: '10px 20px', borderRadius: 50, fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)', background: 'var(--bg-surface)', border: '1px solid var(--border)', cursor: 'pointer', minHeight: 44 }}
        >
          {tr.home.back}
        </button>
        <button
          onClick={onRetry}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 50, fontSize: 14, fontWeight: 600, background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', cursor: 'pointer', minHeight: 44 }}
        >
          <RefreshCw size={14} /> {tr.home.retry}
        </button>
      </div>
    </div>
  )
}

// ── Recent-deck card — "jump back into a deck" (secondary-weight surface) ────────
// Deliberately NOT accent-filled: the Study button stays the only primary CTA.
// Tapping starts a focused Learn session for this deck via the existing route.
function RecentDeckCard({ deck, onOpen }: { deck: RecentDeck; onOpen: (id: string) => void }) {
  const { tr } = useLocale()
  const meta = deck.due_count > 0
    ? tr.home.deckCardsDue(deck.card_count, deck.due_count)
    : tr.home.deckCards(deck.card_count)

  return (
    <button
      onClick={() => onOpen(deck.deck_id)}
      style={{
        display: 'flex', flexDirection: 'column', gap: 8, width: '100%',
        padding: '14px 16px', borderRadius: 16, textAlign: 'left',
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        cursor: 'pointer', fontFamily: 'var(--font-ui)',
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)' }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
        <span style={{
          flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {deck.title}
        </span>
        <ArrowRight size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
      </div>
      <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{meta}</span>
      {/* Wave 3 single-hue magnitude indicator — learned / total, never rainbow/red. */}
      <DeckProgressBar learned={deck.learned_count} total={deck.card_count} width="100%" height={3} />
    </button>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function HomePage() {
  const navigate = useNavigate()
  const user = useAuthStore(selectUser)
  const { tr } = useLocale()

  const { data: summary, isLoading: summaryLoading, isError, refetch } = useProgressSummary()
  // Home Continue is bound to the smart-queue daily stream exclusively (ADR-045).
  // Blocks render until settled to prevent Study→Continue flicker on the first render.
  const { data: sqSession, isLoading: sessionLoading } = useSmartQueueSession()
  // Fetch reviewed/total for the Continue label; staleTime matches sqSession query.
  const { data: sessionStatus, isLoading: sessionStatusLoading } = useSessionStatus(sqSession?.session_id)
  const { data: vocabMap, isLoading: vocabMapLoading } = useVocabularyMap()
  // Recently focus-studied decks with actionable material. The endpoint never
  // returns a placeholder — an empty array simply hides the block below.
  const { data: recentDecks, isLoading: recentDecksLoading } = useRecentDecks()
  const startSmartQueue = useStartSmartQueue()
  const startTopicTrain = useStartTopicTrain()
  const createDeck = useCreateDeck()
  const [pendingTopic, setPendingTopic] = useState<string | null>(null)
  const [nothingToPractiseTopic, setNothingToPractiseTopic] = useState<string | null>(null)
  const [studyError, setStudyError] = useState(false)
  const [showSheet, setShowSheet] = useState(false)
  const addBtnRef = useRef<HTMLButtonElement>(null)
  const [showDeckCreate, setShowDeckCreate] = useState(false)
  const [showCSV, setShowCSV] = useState(false)

  // The screen's own name for the desktop screen-header bar — distinct from
  // (and unrelated to) the personalized greeting DailyContract renders below;
  // that greeting is page content, not the screen's identity, and stays
  // exactly where it is.
  useScreenHeader(useMemo(() => ({ title: tr.nav.home }), [tr.nav.home]))

  // Also block on sessionStatus when a session exists — prevents the Continue→Study
  // flash that would occur if we rendered before knowing whether reviewed === 0.
  const showSkeleton = summaryLoading || sessionLoading || (!!sqSession && sessionStatusLoading)

  // ADR-106 P4: Home keeps its scroll position for the session. It is put
  // back only once the page is past its skeleton and the blocks that load on
  // their own (vocabulary map, recent decks) have settled, so the offset is
  // never clamped against a page that is still growing.
  useStopScroll('home', { ready: !showSkeleton && !vocabMapLoading && !recentDecksLoading })

  if (showSkeleton) return <HomePageSkeleton />
  if (isError || !summary) return <HomePageError onRetry={() => void refetch()} />

  const { review, newCards, total } = computeSessionPreview(summary)
  const hasWork = total > 0
  const isFresh = !hasWork && summary.words_known_count === 0 && summary.today_cards_reviewed === 0

  const weakItems: WeakSpotItem[] = (vocabMap?.clusters ?? [])
    .filter((c) => c.coverage_pct < 75 && c.trainable_cards >= 3)
    .sort((a, b) => a.coverage_pct - b.coverage_pct)
    .slice(0, 5)
    .map((c) => ({ topicName: c.topic, masteryPercent: Math.round(c.coverage_pct), deckId: '' }))

  // The stream's start (ADR-103 Decision 1). The server returns today's live stream
  // session when there is one and builds a new one otherwise, so this is the whole of
  // both "Study" and "Continue" — the client never picks a session id itself.
  function handleStudy() {
    if (startSmartQueue.isPending) return
    setStudyError(false)
    startSmartQueue.mutate(undefined, {
      onSuccess: (data) => {
        if (data.session_id) {
          navigate('/session', {
            state: {
              sessionId: data.session_id,
              totalCards: data.total_cards,
              // Flicker-free counter placeholder on a resume (ADR-046); the Learn
              // page's authoritative status fetch corrects it.
              ...(data.is_resumed ? { resolutionCards: data.resolution_count } : {}),
              dayBoundaryClosed: data.day_boundary_closed_previous,
            },
          })
        } else {
          navigate('/learn')
        }
      },
      onError: () => setStudyError(true),
    })
  }

  function handleTrainTopic(topic: string) {
    if (startTopicTrain.isPending) return
    setPendingTopic(topic)
    setNothingToPractiseTopic(null)
    startTopicTrain.mutate(topic, {
      onSuccess: (data) => {
        navigate('/session', { state: { sessionId: data.session_id, totalCards: data.total_cards, sessionTitle: `Training: ${data.topic}` } })
      },
      // ADR-112: topic training is practice and takes only words already answered.
      // A 422 means this topic has too few of them — explain it in place rather
      // than jump to /learn as if training had started.
      onError: (err) => {
        const httpStatus = (err as { response?: { status?: number } })?.response?.status
        if (httpStatus === 422) setNothingToPractiseTopic(topic)
        else navigate('/learn')
      },
      onSettled: () => setPendingTopic(null),
    })
  }

  // ── Inline session-error screen (coding rule 13) ───────────────────────────
  if (studyError) {
    return (
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '24px 0 0' }}>
        <StudyError onRetry={handleStudy} onBack={() => setStudyError(false)} />
      </div>
    )
  }

  // ── Chameleon CTA — one slot, contextual label ─────────────────────────────

  // Stat line text — used by the Continue state's quiet /queue affordance below the CTA.
  const statsLabel = newCards > 0 && review > 0
    ? tr.home.newAndReview(newCards, review)
    : newCards > 0 ? tr.home.newCards(newCards) : tr.home.reviewCards(review)

  // Label only (ADR-103 Decision 5): a live stream session with no resolution yet reads
  // as "Study", one with resolutions as "Continue · N/M". Either way the button calls
  // the stream start, which returns to that same session — the label reports what the
  // button will do, it does not decide it. Read from resolution_count (ADR-046), never
  // the graduation count, which stays 0 through a whole run of loop-level answers.
  const isZeroProgressSession = !!sqSession && sessionStatus !== undefined && sessionStatus.resolution_count === 0

  let ctaBlock: React.ReactNode

  if (sqSession && !isZeroProgressSession) {
    // Live stream session with resolutions > 0 — the label offers Continue; the
    // button is the stream start either way (ADR-103 Decision 1).
    const done = sessionStatus?.resolution_count ?? null
    const sessionTotal = sessionStatus?.total ?? null
    const label = done !== null && sessionTotal !== null
      ? tr.home.continueProgress(done, sessionTotal)
      : tr.home.continue

    ctaBlock = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
        <button
          onClick={handleStudy}
          disabled={startSmartQueue.isPending}
          style={ctaBtnStyle(startSmartQueue.isPending)}
          onMouseDown={(e) => pressIn(e, startSmartQueue.isPending)}
          onMouseUp={pressOut}
          onMouseLeave={pressOut}
        >
          {startSmartQueue.isPending ? tr.home.starting : label}
          {!startSmartQueue.isPending && <ArrowRight size={22} />}
        </button>
        {/* Quiet queue affordance — /queue must be reachable in every Home state */}
        <button
          onClick={() => navigate('/queue')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 13, color: 'var(--text-secondary)',
            textDecoration: 'underline', textDecorationColor: 'var(--border)',
            textUnderlineOffset: 3,
          }}
        >
          {total > 0 ? statsLabel : tr.home.manageQueue}
        </button>
      </div>
    )
  } else if (hasWork) {
    const mins = minuteEstimate(total)
    // No standalone stat line above the CTA: it competed with the primary button and
    // duplicated the card count already in the label. Review-due cards still flow
    // through this smart-queue session; /queue stays reachable from the other states.
    ctaBlock = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
        <button
          onClick={handleStudy}
          disabled={startSmartQueue.isPending}
          style={ctaBtnStyle(startSmartQueue.isPending)}
          onMouseDown={(e) => pressIn(e, startSmartQueue.isPending)}
          onMouseUp={pressOut}
          onMouseLeave={pressOut}
        >
          {startSmartQueue.isPending
            ? tr.home.starting
            : tr.home.study(total, mins)
          }
          {!startSmartQueue.isPending && <ArrowRight size={22} />}
        </button>
      </div>
    )
  } else if (isFresh) {
    // Empty-stream zero-state: no stream source has any new/due cards. With the
    // system decks seeded this is normally unreachable (the base stream always
    // has new cards); it only shows when nothing is seeded or the user paused
    // every source. Point at the catalog decks + queue, never at courses.
    ctaBlock = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, width: '100%' }}>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
          {tr.home.emptyStream}
        </p>
        <button
          onClick={() => navigate('/catalog')}
          style={ctaBtnStyle(false)}
          onMouseDown={(e) => pressIn(e, false)}
          onMouseUp={pressOut}
          onMouseLeave={pressOut}
        >
          {tr.home.browseDecks} <ArrowRight size={22} />
        </button>
        <button
          onClick={() => navigate('/queue')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 13, color: 'var(--text-secondary)',
            textDecoration: 'underline', textDecorationColor: 'var(--border)',
            textUnderlineOffset: 3,
          }}
        >
          {tr.home.manageStudySources}
        </button>
      </div>
    )
  } else {
    // All caught up — no competing CTAs; quiet link to queue management
    ctaBlock = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 24px', borderRadius: 24, background: 'var(--bg-surface)', border: '1px solid var(--border)', width: '100%', textAlign: 'center' }}>
        <Check size={22} style={{ color: 'var(--text-secondary)' }} />
        <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{tr.home.allCaughtUp}</p>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>{tr.home.nothingDue}</p>
        <button
          onClick={() => navigate('/queue')}
          style={{ marginTop: 4, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, color: 'var(--accent)', fontWeight: 500, minHeight: 44, display: 'flex', alignItems: 'center' }}
        >
          {tr.home.manageStudySources}
        </button>
      </div>
    )
  }

  function handleDeckSave(body: DeckCreate) {
    createDeck.mutate(body, {
      onSuccess: (deck) => {
        setShowDeckCreate(false)
        navigate(`/decks/${deck.id}`, { state: { from: '/home' } })
      },
    })
  }

  // On desktop, widen to a two-column layout only when secondary content exists.
  // Without secondary content (fresh user, no recent decks, no weak spots), the
  // narrow centred column avoids a CTA floating left in a wide void.
  const hasSecondary =
    (recentDecks && recentDecks.length > 0) || (!isFresh && weakItems.length > 0)

  return (
    <>
      {/* ── Main layout — single column on mobile, two-column on desktop ── */}
      <div
        className={
          hasSecondary
            ? 'flex flex-col gap-8 max-w-[480px] mx-auto pt-6 lg:flex-row lg:items-start lg:max-w-[860px] lg:gap-x-10'
            : 'flex flex-col gap-8 max-w-[480px] mx-auto pt-6'
        }
        style={{ color: 'var(--text-primary)' }}
      >
        {/* ── Left / primary column ────────────────────────────────────── */}
        <div className={hasSecondary ? 'flex flex-col gap-8 lg:w-[420px] lg:flex-shrink-0' : 'flex flex-col gap-8'}>
          {/* Daily contract — greeting + reviewed/goal + streak (informational only) */}
          <DailyContract summary={summary} displayName={user?.display_name} userId={user?.id ?? ''} />

          {/* Primary CTA + the round "+" (handoff §1): «Учить» stays the one primary
              action; the "+" beside it opens the «Добавить» sheet. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>{ctaBlock}</div>
            <button
              ref={addBtnRef}
              className="home-add-btn"
              onClick={() => setShowSheet(true)}
              aria-label={tr.addContent.title}
              aria-haspopup="dialog"
              aria-expanded={showSheet}
              data-testid="home-add"
              style={{
                width: 56, height: 56, flexShrink: 0, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--bg-surface)', border: '2px solid var(--border)',
                color: 'var(--text-primary)', cursor: 'pointer',
              }}
            >
              <Plus size={22} />
            </button>
            <style>{`.home-add-btn:hover,.home-add-btn:active,.home-add-btn[aria-expanded="true"]{border-color:var(--accent)!important;color:var(--accent)!important}`}</style>
          </div>
        </div>

        {/* ── Right / secondary column — stacks below primary on mobile ── */}
        {hasSecondary && (
          <div className="flex flex-col gap-8 flex-1 min-w-0">
            {/* Recently studied decks — jump straight back into a focused session.
                Rendered ONLY when the endpoint returns decks with actionable material. */}
            {recentDecks && recentDecks.length > 0 && (
              <div>
                <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, color: 'var(--text-secondary)' }}>
                  {tr.home.jumpBackIn}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {recentDecks.map((deck) => (
                    <RecentDeckCard
                      key={deck.deck_id}
                      deck={deck}
                      onOpen={(id) => navigate(`/decks/${id}/learn`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Weak Spots — only when real error data exists, never in zero-state */}
            {!isFresh && weakItems.length > 0 && (
              <div>
                <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, color: 'var(--text-secondary)' }}>
                  {tr.home.weakSpots}
                </p>
                <WeakSpots
                  items={weakItems}
                  pendingTopic={pendingTopic}
                  onTrainTopic={handleTrainTopic}
                  nothingToPractiseTopic={nothingToPractiseTopic}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modals / sheets — portals, zero layout footprint ── */}
      <AddContentSheet
        open={showSheet}
        onClose={() => setShowSheet(false)}
        onCreateDeck={() => setShowDeckCreate(true)}
        onImportCSV={() => setShowCSV(true)}
        anchorRef={addBtnRef}
      />
      {showDeckCreate && (
        <DeckCreateModal
          onSave={handleDeckSave}
          onClose={() => setShowDeckCreate(false)}
          isSaving={createDeck.isPending}
        />
      )}
      <CSVImportModal
        isOpen={showCSV}
        onClose={() => setShowCSV(false)}
      />
    </>
  )
}
